import styled, { keyframes, css } from "styled-components";
import { radius } from "@libertify/ui";

// ─── Keyframes ───────────────────────────────────────────────

export const floatOrb1 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-15px, -20px) scale(1.02); }
`;

export const floatOrb2 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(25px, -15px) scale(0.97); }
`;

export const floatOrb3 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-10px, 20px) scale(1.03); }
`;

export const rotateRing = keyframes`
  from { transform: rotateZ(0deg); }
  to { transform: rotateZ(360deg); }
`;

export const rotateRingReverse = keyframes`
  from { transform: rotateZ(0deg); }
  to { transform: rotateZ(-360deg); }
`;

export const pulseDot = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.3); }
`;

export const floatParticle = keyframes`
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  10% { opacity: 0.6; }
  90% { opacity: 0.6; }
  100% {
    transform: translateY(-200px);
    opacity: 0;
  }
`;

export const pulseGlow = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.15); opacity: 0.8; }
`;

export const shimmerLine = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.8; }
  100% { opacity: 0.4; }
`;

export const pulseBar = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(1.15); }
`;

// ─── Page-level background (used by slides-streaming.tsx) ────

export const PageGradientBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgb(248, 250, 252),
    rgba(239, 246, 255, 0.3),
    rgba(238, 242, 255, 0.4)
  );
  pointer-events: none;
  z-index: 0;
`;

export const PageNoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.015;
  pointer-events: none;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
`;

// ─── Layout ──────────────────────────────────────────────────

export const AnimationContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const FloatingOrb = styled.div<{
  $size: number;
  $color: string;
  $top?: string;
  $bottom?: string;
  $left?: string;
  $right?: string;
  $animationIndex: 1 | 2 | 3;
}>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${radius.circle};
  background-color: ${({ $color }) => $color};
  filter: blur(48px);
  ${({ $top }) => $top && `top: ${$top};`}
  ${({ $bottom }) => $bottom && `bottom: ${$bottom};`}
  ${({ $left }) => $left && `left: ${$left};`}
  ${({ $right }) => $right && `right: ${$right};`}
  animation: ${({ $animationIndex }) =>
    $animationIndex === 1
      ? floatOrb1
      : $animationIndex === 2
        ? floatOrb2
        : floatOrb3}
    12s ease-in-out infinite;
`;

export const ContentWrapper = styled.div`
  width: 100%;
  max-width: 512px;
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
`;

// ─── Orbital System ──────────────────────────────────────────

export const OrbitalContainer = styled.div`
  position: relative;
  width: 320px;
  height: 256px;
  margin-bottom: 40px;
`;

export const OrbitalRing = styled.div<{
  $size: number;
  $duration: number;
  $reverse?: boolean;
  $borderColor?: string;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  margin-top: ${({ $size }) => -$size / 2}px;
  margin-left: ${({ $size }) => -$size / 2}px;
  border: 1px solid
    ${({ $borderColor }) => $borderColor || "rgba(191, 219, 254, 0.3)"};
  border-radius: ${radius.circle};
  animation: ${({ $reverse }) => ($reverse ? rotateRingReverse : rotateRing)}
    ${({ $duration }) => $duration}s linear infinite;
`;

export const RingDot = styled.div<{
  $size: number;
  $gradient: string;
  $shadow: string;
  $position: "top" | "bottom" | "right";
}>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${radius.circle};
  background: ${({ $gradient }) => $gradient};
  box-shadow: ${({ $shadow }) => $shadow};
  animation: ${pulseDot} 2s ease-in-out infinite;

  ${({ $position, $size }) => {
    switch ($position) {
      case "top":
        return css`
          top: ${-$size / 2}px;
          left: 50%;
          margin-left: ${-$size / 2}px;
        `;
      case "bottom":
        return css`
          bottom: ${-$size / 2}px;
          left: 50%;
          margin-left: ${-$size / 2}px;
        `;
      case "right":
        return css`
          top: 50%;
          right: ${-$size / 2}px;
          margin-top: ${-$size / 2}px;
        `;
    }
  }}
`;

// ─── Visual Scene Container (3D transitions) ─────────────────

export const VisualSceneWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
`;

// ─── Scene 0: Document/Browser Window ────────────────────────

export const DocCard = styled.div`
  width: 160px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-radius: ${radius.box}px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow:
    0 20px 40px -12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
`;

export const DocTrafficLights = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
`;

export const DocDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  background-color: ${({ $color }) => $color};
`;

export const DocLine = styled.div<{ $width: string; $delay: number }>`
  height: 8px;
  width: ${({ $width }) => $width};
  background: linear-gradient(
    to right,
    rgba(199, 210, 254, 0.6),
    rgba(191, 219, 254, 0.4)
  );
  border-radius: 4px;
  margin-bottom: 8px;
  animation: ${shimmerLine} 2s ease-in-out ${({ $delay }) => $delay}s infinite;

  &:last-child {
    margin-bottom: 0;
  }
`;

// ─── Scene 1: Chat/Messaging ─────────────────────────────────

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 180px;
`;

export const ChatBubble = styled.div<{
  $align: "left" | "right";
  $color: string;
  $width: string;
}>`
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 0;
  width: ${({ $width }) => $width};
  height: 14px;
  background: ${({ $color }) => $color};
  align-self: ${({ $align }) =>
    $align === "left" ? "flex-start" : "flex-end"};
  border-bottom-left-radius: ${({ $align }) =>
    $align === "left" ? "4px" : "16px"};
  border-bottom-right-radius: ${({ $align }) =>
    $align === "right" ? "4px" : "16px"};
  box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.08);
`;

// ─── Scene 2: Profile/Avatar Card ────────────────────────────

export const ProfileCard = styled.div`
  width: 180px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-radius: ${radius.box}px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow:
    0 20px 40px -12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const ProfileAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${radius.circle};
  background: linear-gradient(135deg, rgb(96, 165, 250), rgb(34, 211, 238));
  position: relative;
`;

export const ProfileStatusDot = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: ${radius.circle};
  background: rgb(52, 211, 153);
  border: 2px solid white;
`;

export const ProfileLine = styled.div<{ $width: string }>`
  height: 8px;
  width: ${({ $width }) => $width};
  background: rgba(226, 232, 240, 0.8);
  border-radius: 4px;
`;

// ─── Scene 3: Bar Chart/Analytics ────────────────────────────

export const ChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 96px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  border-radius: ${radius.box}px;
  border: 1px solid rgba(226, 232, 240, 0.4);
`;

export const ChartBar = styled.div<{
  $height: string;
  $color: string;
  $delay: number;
}>`
  width: 20px;
  height: ${({ $height }) => $height};
  background: ${({ $color }) => $color};
  border-radius: 4px 4px 0 0;
  transform-origin: bottom;
  animation: ${pulseBar} 2.5s ease-in-out ${({ $delay }) => $delay}s infinite;
`;

// ─── Particles ───────────────────────────────────────────────

export const ParticlesContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

export const Particle = styled.div<{
  $left: number;
  $delay: number;
  $duration: number;
  $size: number;
}>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${radius.circle};
  background-color: rgba(96, 165, 250, 0.6);
  filter: blur(0.5px);
  left: ${({ $left }) => $left}%;
  bottom: 0;
  animation: ${floatParticle} ${({ $duration }) => $duration}s ease-in
    ${({ $delay }) => $delay}s infinite;
`;

export const RadialGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 200%;
  height: 200%;
  transform: translate(-50%, -50%);
  border-radius: ${radius.circle};
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.08) 0%,
    transparent 70%
  );
  pointer-events: none;
  animation: ${pulseGlow} 4s ease-in-out infinite;
`;

// ─── Step List ───────────────────────────────────────────────

export const StepCarouselContainer = styled.div`
  width: 100%;
  position: relative;
  height: 180px;
  perspective: 800px;
  overflow: hidden;
`;

export const StepItem = styled.div<{ $isActive: boolean; $isPast: boolean }>`
  position: relative;
  padding: 12px 16px;
  border-radius: ${radius.box}px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StepHighlight = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(239, 246, 255, 0.8),
    rgba(238, 242, 255, 0.5)
  );
  border-radius: ${radius.box}px;
  border: 1px solid rgba(191, 219, 254, 0.5);
`;

export const StepIconWrapper = styled.div<{ $isActive: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  ${({ $isActive }) =>
    $isActive
      ? css`
          background: linear-gradient(
            to bottom right,
            rgb(59, 130, 246),
            rgb(99, 102, 241)
          );
          box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.25);
          color: white;
          font-size: 16px;
        `
      : css`
          background: rgba(226, 232, 240, 0.8);
          color: rgba(148, 163, 184, 1);
          font-size: 16px;
        `}
`;

export const StepLabel = styled.span<{
  $isActive: boolean;
}>`
  font-size: 15px;
  letter-spacing: -0.01em;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  color: ${({ $isActive }) =>
    $isActive ? "rgba(30, 41, 59, 1)" : "rgba(100, 116, 139, 1)"};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
`;

export const ActiveDots = styled.div`
  margin-left: auto;
  display: flex;
  gap: 2px;
  position: relative;
  z-index: 1;
`;

export const ActiveDot = styled.div<{ $index: number }>`
  width: 4px;
  height: 4px;
  border-radius: ${radius.circle};
  background-color: rgb(96, 165, 250);
  animation: ${pulseDot} 1.5s ease-in-out ${({ $index }) => $index * 0.2}s
    infinite;
`;

// ─── Progress Dots ───────────────────────────────────────────

export const ProgressDotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 48px;
`;

export const ProgressDot = styled.div<{ $isActive: boolean }>`
  border-radius: ${radius.pill}px;
  transition: all 0.5s ease;
  height: 8px;

  ${({ $isActive }) =>
    $isActive
      ? css`
          width: 24px;
          background: linear-gradient(
            to right,
            rgb(59, 130, 246),
            rgb(99, 102, 241)
          );
        `
      : css`
          width: 8px;
          background-color: rgb(226, 232, 240);
        `}
`;

// ─── Wrapper for MainPanel integration ───────────────────────

export const LoadingAnimationWrapper = styled.div`
  align-items: center;
  justify-content: center;
  min-height: 0;
  transition: all 0.3s ease;
`;
